import React, { useState, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import '../styles/QRScannerPage.css';

const TIERS = [
  { label: '5 – 20 ₼',  range: '5-20',  points: 10, color: '#3B82F6', bg: '#EFF6FF' },
  { label: '20 – 50 ₼', range: '20-50', points: 35, color: '#F59E0B', bg: '#FFFBEB' },
  { label: '50+ ₼',     range: '50+',   points: 80, color: '#10B981', bg: '#ECFDF5' },
];

const TIER_COLOR = { '5-20': '#3B82F6', '20-50': '#F59E0B', '50+': '#10B981' };
const TIER_LABEL = { '5-20': '5–20 ₼ · 10 xal', '20-50': '20–50 ₼ · 35 xal', '50+': '50+ ₼ · 80 xal' };

const QRScannerPage = () => {
  const { business } = useAuth();
  const { activeLocation } = useLocation();
  const [scanning, setScanning]       = useState(false);
  const [manualCode, setManualCode]   = useState('');
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [visitLogs, setVisitLogs]     = useState([]);
  const [couponLogs, setCouponLogs]   = useState([]);
  const [activeTab, setActiveTab]     = useState('visits'); // 'visits' | 'coupons'
  const [step, setStep]               = useState('scan'); // 'scan' | 'tier' | 'wallet' | 'done'
  const [pendingUser, setPendingUser] = useState(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletIdempotencyKey, setWalletIdempotencyKey] = useState(null);

  let html5QrCode = null;

  // ── Load logs ──────────────────────────────────────────────────────────────
  const loadLogs = async () => {
    if (!business?.id) return;

    const { data: vLogs } = await supabase
      .from('point_logs')
      .select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false })
      .limit(20);
    setVisitLogs(vLogs || []);

    const { data: cLogs, error: cErr } = await supabase
      .rpc('get_coupon_logs', { p_business_id: business.id });
    if (cErr) console.error('coupon_logs rpc error:', cErr.message);
    setCouponLogs(cLogs || []);
  };

  useEffect(() => {
    loadLogs();
    return () => { if (scanning) stopScanner(); };
  }, [business?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scanner ────────────────────────────────────────────────────────────────
  const startScanner = async () => {
    try {
      html5QrCode = new Html5Qrcode("qr-reader");
      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onScanSuccess,
        () => {}
      );
      setScanning(true);
      setError('');
    } catch (err) {
      setError('Kamera açıla bilmədi. Brauzer icazəsi verməlisiniz.');
    }
  };

  const stopScanner = async () => {
    if (html5QrCode) {
      try { await html5QrCode.stop(); html5QrCode.clear(); } catch (_) {}
      setScanning(false);
    }
  };

  const onScanSuccess = (decodedText) => {
    stopScanner();
    if (decodedText.startsWith('VISIT-')) identifyUser(decodedText);
    else verifyCoupon(decodedText);
  };

  // ── Visit flow ─────────────────────────────────────────────────────────────
  const identifyUser = async (qrValue) => {
    setLoading(true); setError('');
    try {
      const userId = qrValue.replace('VISIT-', '');
      if (!userId || userId.length < 10) throw new Error('Yanlış QR kod');

      const { data: prof, error: profErr } = await supabase.rpc('get_customer_scan_info', {
        p_user_id: userId, p_business_location_id: activeLocation?.id || business?.id,
      });
      if (profErr || !prof?.success) throw new Error('İstifadəçi tapılmadı');

      let corporateInfo = null;
      if (prof.is_corporate) {
        corporateInfo = {
          companyName: prof.company_name,
          isFriendly: prof.is_friendly,
          discount: prof.discount,
          balanceId: prof.balance_id,
          balance: prof.balance,
          monthlyLimit: prof.monthly_limit,
          partnerId: prof.partner_id,
        };
      }

      setPendingUser({ userId, userName: prof.full_name || 'İstifadəçi', currentPoints: prof.points || 0, corporateInfo });
      setStep('tier');
    } catch (err) {
      setError(err.message || 'Xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const awardPoints = async (tier) => {
    setLoading(true); setError('');
    try {
      const { userId, userName } = pendingUser;

      const { data, error: rpcErr } = await supabase.rpc('scan_visit_qr', {
        p_user_id: userId, p_business_id: business?.id, p_points: tier.points,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!data?.success) throw new Error(data?.error || 'Xal verilə bilmədi');

      await supabase.from('point_logs').insert({
        business_id: business?.id,
        user_id: userId,
        user_name: userName,
        amount_tier: tier.range,
        points: tier.points,
      });

      const { data: prof } = await supabase
        .from('public_user_profiles').select('points').eq('id', userId).single();

      setResult({
        type: 'visit',
        userName,
        pointsAwarded: tier.points,
        tierLabel: tier.label,
        totalPoints: prof?.points ?? (pendingUser.currentPoints + tier.points),
      });
      setStep('done');
      loadLogs();
    } catch (err) {
      setError(err.message || 'Xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  // ── Coupon flow ────────────────────────────────────────────────────────────
  const verifyCoupon = async (qrCode) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const { data: lookupRes, error: couponError } = await supabase.rpc('lookup_coupon_by_qr', { p_qr_code: qrCode });

      if (couponError || !lookupRes?.success) throw new Error('Kupon tapılmadı');
      const coupon = lookupRes.coupon;
      if (coupon.is_used) throw new Error('Bu kupon artıq istifadə olunub');
      if (new Date(coupon.expires_at) < new Date()) throw new Error('Kuponun müddəti bitib');

      setResult({ type: 'coupon', coupon, user: lookupRes.user });
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmCoupon = async () => {
    if (!result?.coupon) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('confirm_coupon', {
        p_coupon_id:          result.coupon.id,
        p_business_id:        business?.id,
        p_user_name:          result.user?.full_name || 'İstifadəçi',
        p_coupon_title:       result.coupon.title,
        p_coupon_description: result.coupon.description || '',
      });

      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Xəta baş verdi');

      setResult(prev => ({ ...prev, confirmed: true }));
      loadLogs();
      setActiveTab('coupons');
    } catch (err) {
      alert('❌ Xəta baş verdi: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('scan'); setResult(null);
    setPendingUser(null); setManualCode(''); setError(''); setWalletAmount(''); setWalletIdempotencyKey(null);
  };

  const applyDiscount = async () => {
    const ci = pendingUser?.corporateInfo;
    if (!ci) return;
    setLoading(true); setError('');
    try {
      await supabase.from('corporate_discount_logs').insert({
        user_id:              pendingUser.userId,
        business_location_id: activeLocation?.id || business?.id,
        corporate_partner_id: ci.partnerId,
        discount_percent:     ci.discount,
      });
      setResult({
        type: 'discount',
        userName: pendingUser.userName,
        companyName: ci.companyName,
        discount: ci.discount,
      });
      setStep('done');
      loadLogs();
    } catch (err) {
      setError(err.message || 'Xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const WALLET_ERROR_MESSAGES = {
    INVALID_AMOUNT: 'Düzgün məbləğ daxil edin',
    NOT_AUTHORIZED: 'Bu əməliyyat üçün icazəniz yoxdur',
    NO_BALANCE_FOUND: 'Bu ay üçün balans tapılmadı',
    INSUFFICIENT_BALANCE: 'Balans kifayət deyil',
    PARTNER_INACTIVE: 'Bu şirkətin müqaviləsi deaktivdir, balans xərclənə bilməz',
  };

  const deductWalletBalance = async () => {
    const amount = parseFloat(walletAmount);
    if (!amount || amount <= 0) { setError('Düzgün məbləğ daxil edin'); return; }
    const ci = pendingUser?.corporateInfo;
    if (!ci?.balanceId) { setError('Bu ay üçün balans tapılmadı'); return; }
    if (amount > ci.balance) { setError(`Balans kifayət deyil (${ci.balance.toFixed(2)} ₼ var)`); return; }

    setLoading(true); setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('deduct_corporate_balance', {
        p_user_id: pendingUser.userId,
        p_business_location_id: activeLocation?.id || business?.id,
        p_amount: amount,
        p_idempotency_key: walletIdempotencyKey,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!data?.success) throw new Error(WALLET_ERROR_MESSAGES[data?.error] || data?.error || 'Xəta baş verdi');

      setResult({
        type: 'wallet',
        userName: pendingUser.userName,
        amount,
        balanceBefore: ci.balance,
        balanceAfter: data.new_balance,
        companyName: ci.companyName,
      });
      setStep('done');
      loadLogs();
    } catch (err) {
      setError(err.message || 'Xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerify = () => {
    if (!manualCode.trim()) { setError('QR kod daxil edin'); return; }
    const code = manualCode.trim();
    if (code.startsWith('VISIT-')) identifyUser(code);
    else verifyCoupon(code);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scanner-page">
      <div className="scanner-header">
        <h1>🔍 QR Kod Skaneri</h1>
        <p>Müştəri QR kodlarını skan edin</p>
      </div>

      {/* ── STEP: SCAN ── */}
      {step === 'scan' && (
        <div className="scanner-container">
          <div className="scanner-card">
            <h2>Kamera ilə Skan</h2>
            <div id="qr-reader" className="qr-reader"></div>
            {!scanning
              ? <button className="scan-button" onClick={startScanner}>📷 Kameranı Aç</button>
              : <button className="scan-button stop" onClick={stopScanner}>⏹️ Dayandır</button>
            }
            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-message">Yoxlanılır...</div>}
          </div>

          <div className="manual-input-card">
            <h2>və ya Manual Daxil Et</h2>
            <div className="manual-input-group">
              <input type="text" value={manualCode}
                onChange={e => setManualCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleManualVerify()}
                placeholder="QR kod daxil edin" className="manual-input" />
              <button className="verify-button" onClick={handleManualVerify}
                disabled={loading || !manualCode.trim()}>
                {loading ? 'Yoxlanılır...' : 'Yoxla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: TIER SELECT ── */}
      {step === 'tier' && pendingUser && (
        <div className="coupon-result">
          <div className="result-card success">
            <div className="result-header">
              <h2>{pendingUser.corporateInfo ? '🏢 Korporativ Müştəri' : '👤 Müştəri Tanındı'}</h2>
            </div>
            <div className="result-user">
              <div className="result-avatar-placeholder">{pendingUser.userName[0]}</div>
              <h3>{pendingUser.userName}</h3>
              {pendingUser.corporateInfo ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#f5f3ff', border: '1.5px solid #c4b5fd',
                  borderRadius: 10, padding: '8px 14px', marginTop: 8,
                }}>
                  <span style={{ fontSize: 20 }}>🏢</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#4c1d95' }}>
                    {pendingUser.corporateInfo.companyName} əməkdaşı
                  </span>
                </div>
              ) : (
                <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
                  Cari balı: {pendingUser.currentPoints} xal
                </p>
              )}
            </div>

            {/* ── KORPORATİV PANEL ── */}
            {pendingUser.corporateInfo && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '16px 0' }}>

                {/* Endirim */}
                {pendingUser.corporateInfo.isFriendly && pendingUser.corporateInfo.discount > 0 && (
                  <div style={{
                    background: '#fdf4ff', border: '2px solid #e879f9',
                    borderRadius: 14, padding: '16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#86198f' }}>
                          🏷️ {pendingUser.corporateInfo.discount}% Korporativ Endirim
                        </div>
                        <div style={{ fontSize: 12, color: '#a21caf', marginTop: 3 }}>
                          Fiziki hesabda endirimi tətbiq edin
                        </div>
                      </div>
                    </div>
                    <button onClick={applyDiscount} disabled={loading} style={{
                      width: '100%', padding: '11px', borderRadius: 9, border: 'none',
                      background: loading ? '#e2e8f0' : '#c026d3',
                      color: loading ? '#94a3b8' : '#fff',
                      fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                    }}>
                      {loading ? 'İşlənir...' : `✅ ${pendingUser.corporateInfo.discount}% Endirimi Tətbiq Et`}
                    </button>
                  </div>
                )}

                {/* Yemək büdcəsi */}
                {pendingUser.corporateInfo.balanceId && (
                  <div style={{
                    background: '#f5f3ff', border: '2px solid #c4b5fd',
                    borderRadius: 14, padding: '16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#4c1d95' }}>💳 Yemək Büdcəsi</div>
                        <div style={{ fontSize: 13, color: '#7c3aed', marginTop: 3 }}>
                          {pendingUser.corporateInfo.balance.toFixed(2)} ₼ / {pendingUser.corporateInfo.monthlyLimit.toFixed(2)} ₼
                        </div>
                      </div>
                    </div>
                    <button onClick={() => { setError(''); setWalletIdempotencyKey(crypto.randomUUID()); setStep('wallet'); }} disabled={loading} style={{
                      width: '100%', padding: '11px', borderRadius: 9, border: 'none',
                      background: '#6c63ff', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}>
                      💳 Balansdan Ödə
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── STANDART XAL TİER (yalnız qeyri-korporativ) ── */}
            {!pendingUser.corporateInfo && (
              <>
                <div style={{ margin: '20px 0 8px', fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                  💳 Ödəniş məbləğini seçin:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {TIERS.map(tier => (
                    <button key={tier.range} onClick={() => awardPoints(tier)} disabled={loading}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px 20px', borderRadius: 14, border: `2px solid ${tier.color}`,
                        background: tier.bg, cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1,
                      }}>
                      <span style={{ fontWeight: 800, fontSize: 16, color: tier.color }}>{tier.label}</span>
                      <span style={{ background: tier.color, color: '#fff', padding: '4px 14px', borderRadius: 20, fontWeight: 800, fontSize: 14 }}>
                        +{tier.points} xal
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
            {loading && <div className="loading-message">İşlənir...</div>}
            <button className="cancel-button" onClick={reset} style={{ marginTop: 16, width: '100%' }}>← Geri</button>
          </div>
        </div>
      )}

      {/* ── STEP: WALLET ── */}
      {step === 'wallet' && pendingUser && (
        <div className="coupon-result">
          <div className="result-card success">
            <div className="result-header"><h2>💳 Yemək Büdcəsindən Ödəniş</h2></div>
            <div className="result-user">
              <div className="result-avatar-placeholder">{pendingUser.userName[0]}</div>
              <h3>{pendingUser.userName}</h3>
              <p style={{ color: '#7c3aed', fontWeight: 700, margin: '4px 0 0' }}>
                Balans: {pendingUser.corporateInfo?.balance?.toFixed(2)} ₼
              </p>
            </div>
            <div style={{ margin: '20px 0 8px', fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
              Məbləği daxil edin (₼):
            </div>
            <input
              type="number" min="0.01" step="0.01"
              value={walletAmount}
              onChange={e => setWalletAmount(e.target.value)}
              placeholder="məs: 25.50"
              style={{
                width: '100%', padding: '14px', borderRadius: 10, fontSize: 18, fontWeight: 700,
                border: '2px solid #c4b5fd', outline: 'none', textAlign: 'center', boxSizing: 'border-box',
                color: '#4c1d95', marginBottom: 16,
              }}
            />
            {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
            <button onClick={deductWalletBalance} disabled={loading || !walletAmount} style={{
              width: '100%', padding: '13px', borderRadius: 10, border: 'none',
              background: loading || !walletAmount ? '#e2e8f0' : '#6c63ff',
              color: loading || !walletAmount ? '#94a3b8' : '#fff',
              fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10,
            }}>
              {loading ? 'İşlənir...' : '✅ Ödənişi Təsdiqlə'}
            </button>
            <button onClick={() => { setStep('tier'); setError(''); setWalletAmount(''); }} style={{
              width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #ddd',
              background: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#555',
            }}>← Geri</button>
          </div>
        </div>
      )}

      {/* ── STEP: DONE (discount) ── */}
      {step === 'done' && result?.type === 'discount' && (
        <div className="coupon-result">
          <div className="result-card success">
            <div style={{ fontSize: 52, marginBottom: 12 }}>🏷️</div>
            <h2 style={{ color: '#86198f', marginBottom: 8 }}>Endirim Tətbiq Edildi</h2>
            <p style={{ fontSize: 15, color: '#555', marginBottom: 6 }}>
              <strong>{result.userName}</strong> — {result.companyName}
            </p>
            <div style={{
              background: '#fdf4ff', border: '2px solid #e879f9',
              borderRadius: 14, padding: '18px', margin: '16px 0',
            }}>
              <div style={{ fontSize: 36, fontWeight: 900, color: '#c026d3' }}>
                {result.discount}%
              </div>
              <div style={{ fontSize: 13, color: '#a21caf', marginTop: 6 }}>
                Korporativ endirim fiziki hesaba tətbiq edilsin
              </div>
            </div>
            <button className="scan-button" onClick={reset} style={{ width: '100%' }}>🔍 Yeni Skan</button>
          </div>
        </div>
      )}

      {/* ── STEP: DONE (wallet) ── */}
      {step === 'done' && result?.type === 'wallet' && (
        <div className="coupon-result">
          <div className="result-card success">
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ color: '#4c1d95', marginBottom: 8 }}>Ödəniş Tamamlandı</h2>
            <p style={{ fontSize: 16, color: '#555', marginBottom: 6 }}>
              <strong>{result.userName}</strong> — {result.companyName}
            </p>
            <div style={{ background: '#f5f3ff', borderRadius: 12, padding: '16px 20px', margin: '16px 0', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#7c3aed', fontSize: 13 }}>Çıxılan məbləğ</span>
                <span style={{ fontWeight: 800, color: '#4c1d95', fontSize: 16 }}>−{result.amount.toFixed(2)} ₼</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#7c3aed', fontSize: 13 }}>Qalan balans</span>
                <span style={{ fontWeight: 800, color: '#6c63ff', fontSize: 16 }}>{result.balanceAfter.toFixed(2)} ₼</span>
              </div>
            </div>
            <button className="scan-button" onClick={reset} style={{ width: '100%' }}>🔍 Yeni Skan</button>
          </div>
        </div>
      )}

      {/* ── STEP: DONE (visit) ── */}
      {step === 'done' && result?.type === 'visit' && (
        <div className="coupon-result">
          <div className="result-card success">
            <div className="result-header"><h2>⭐ Xal Verildi!</h2></div>
            <div className="result-user">
              <div className="result-avatar-placeholder">{result.userName[0]}</div>
              <h3>{result.userName}</h3>
            </div>
            <div className="result-details">
              <div className="result-item">
                <span className="result-label">Ödəniş tipi:</span>
                <span className="result-value">{result.tierLabel}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Verilən xal:</span>
                <span className="result-value" style={{ color: '#F0941F', fontWeight: 800 }}>+{result.pointsAwarded} xal</span>
              </div>
              <div className="result-item">
                <span className="result-label">Ümumi xal:</span>
                <span className="result-value">{result.totalPoints} xal</span>
              </div>
            </div>
            <div className="result-actions">
              <button className="confirm-button" onClick={reset}>✅ Yeni Skan</button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: DONE (coupon) ── */}
      {step === 'done' && result?.type === 'coupon' && (
        <div className="coupon-result">
          <div className="result-card success">

            {result.confirmed ? (
              /* ── Confirmed state ── */
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 64, marginBottom: 12 }}>✅</div>
                <h2 style={{ color: '#10B981', marginBottom: 8 }}>Kupon Təsdiqləndi!</h2>
                <p style={{ color: '#64748B', marginBottom: 8 }}>
                  <strong>{result.coupon.title}</strong> kuponu uğurla istifadə edildi.
                </p>
                <p style={{ color: '#94A3B8', fontSize: 13 }}>
                  Müştəri: {result.user?.full_name || 'İstifadəçi'}
                </p>
                <button className="confirm-button" onClick={reset} style={{ marginTop: 20, width: '100%' }}>
                  🔍 Yeni Skan
                </button>
              </div>
            ) : (
              /* ── Verify state ── */
              <>
                <div className="result-header">
                  <h2>🎟️ Kupon Tapıldı</h2>
                </div>

                {/* Müştəri */}
                <div className="result-user">
                  {result.user?.avatar_url
                    ? <img src={av(result.user.avatar_url)} alt="" className="result-avatar" />
                    : <div className="result-avatar-placeholder">{result.user?.full_name?.[0] || '?'}</div>
                  }
                  <h3>{result.user?.full_name || 'İstifadəçi'}</h3>
                </div>

                {/* Kupon məlumatı — əsas bölmə */}
                <div style={{
                  background: 'linear-gradient(135deg, #6D28D9, #E63950)',
                  borderRadius: 16, padding: '20px 20px', marginBottom: 16, color: '#fff',
                }}>
                  <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 6, fontWeight: 600 }}>KUPON</div>
                  <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>{result.coupon.title}</div>
                  {result.coupon.description && (
                    <div style={{ fontSize: 14, opacity: 0.85 }}>{result.coupon.description}</div>
                  )}
                </div>

                <div className="result-details">
                  <div className="result-item">
                    <span className="result-label">QR Kod:</span>
                    <span className="result-value qr-code" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {result.coupon.qr_code}
                    </span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Son İstifadə:</span>
                    <span className="result-value">
                      {new Date(result.coupon.expires_at).toLocaleDateString('az-AZ')}
                    </span>
                  </div>
                  <div className="result-item">
                    <span className="result-label">Xal xərclənib:</span>
                    <span className="result-value" style={{ color: '#E63950', fontWeight: 800 }}>
                      {result.coupon.points_spent || '—'} xal
                    </span>
                  </div>
                </div>

                <div className="result-actions">
                  <button className="confirm-button" onClick={confirmCoupon} disabled={loading}>
                    {loading ? 'Təsdiqlənir...' : '✅ Kuponu Təsdiqlə'}
                  </button>
                  <button className="cancel-button" onClick={reset} disabled={loading}>❌ Ləğv Et</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── LOG TABS ── */}
      {(visitLogs.length > 0 || couponLogs.length > 0) && (
        <div style={{ maxWidth: 560, margin: '32px auto 0', padding: '0 16px' }}>
          {/* Tab header */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => setActiveTab('visits')}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none',
                background: activeTab === 'visits' ? '#1a1a2e' : '#F1F5F9',
                color: activeTab === 'visits' ? '#fff' : '#64748B',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>
              👤 Ziyarətlər ({visitLogs.length})
            </button>
            <button onClick={() => setActiveTab('coupons')}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none',
                background: activeTab === 'coupons' ? '#E63950' : '#F1F5F9',
                color: activeTab === 'coupons' ? '#fff' : '#64748B',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>
              🎟️ Kuponlar ({couponLogs.length})
            </button>
          </div>

          {/* Visit logs */}
          {activeTab === 'visits' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visitLogs.map(log => {
                const color = TIER_COLOR[log.amount_tier] || '#888';
                const tierLabel = TIER_LABEL[log.amount_tier] || log.amount_tier;
                const d = new Date(log.created_at);
                return (
                  <div key={log.id} style={{
                    background: '#fff', borderRadius: 12, padding: '12px 16px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'center', gap: 14,
                  }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, background: color + '18',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, flexShrink: 0,
                    }}>👤</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.user_name}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                        {d.toLocaleDateString('az-AZ')} · {d.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color, background: color + '18', padding: '3px 10px', borderRadius: 20, marginBottom: 4 }}>{tierLabel}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#F0941F' }}>+{log.points} xal</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Coupon logs */}
          {activeTab === 'coupons' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {couponLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94A3B8', fontSize: 14 }}>
                  Hələ kupon oxudulmayıb
                </div>
              ) : couponLogs.map(log => {
                const d = new Date(log.created_at);
                return (
                  <div key={log.id} style={{
                    background: '#fff', borderRadius: 12, padding: '14px 16px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                  }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, background: '#FEE2E2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, flexShrink: 0,
                    }}>🎟️</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.coupon_title}</div>
                      {log.coupon_description && (
                        <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{log.coupon_description}</div>
                      )}
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                        👤 {log.user_name} · {d.toLocaleDateString('az-AZ')} {d.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: '#10B981',
                      background: '#ECFDF5', padding: '4px 10px', borderRadius: 20, flexShrink: 0,
                    }}>
                      ✓ İstifadə edildi
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRScannerPage;
